import React, { useState } from "react";
import axios from "axios";
import { useNavigate, useSearchParams } from "react-router-dom";
import { FaEye, FaEyeSlash } from "react-icons/fa";
import { useAuth } from "../context/AuthContext";
import ResetPassword from "./ResetPassword";


import { GoogleOAuthProvider, GoogleLogin } from '@react-oauth/google';

const GOOGLE_CLIENT_ID = "151808131440-2mbq1163sb3i49fkao08225v2ea0rg5p.apps.googleusercontent.com"; // Replace with your Google client ID

const Account = () => {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // State for active form and form fields
  const [activeForm, setActiveForm] = useState(
    searchParams.get("uid") && searchParams.get("token") ? "reset" : "login"
  );
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    password: "",
    confirmPassword: "",
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  // Register user handler
  const handleRegister = async (e) => {
    e.preventDefault();
    if (formData.password !== formData.confirmPassword) {
      alert("Passwords do not match.");
      return;
    }
    try {
      // Omit confirmPassword before sending to backend
      const { confirmPassword, ...registerData } = formData;
      await axios.post(`${process.env.REACT_APP_API_URL}/register/`, registerData);
      alert("User registered successfully!");
      setActiveForm("login"); // Switch to login after registration
    } catch (error) {
      const errorData = error.response?.data;
      if (errorData) {
        // Construct a readable error message from DRF's errors
        const messages = Object.keys(errorData)
          .map(key => `${key}: ${errorData[key].join(", ")}`)
          .join("\n");
        alert(`Registration failed:\n${messages}`);
      } else {
        alert("Registration failed. Check details.");
      }
    }
  };

  // Login user handler
  const handleLogin = async (e) => {
    e.preventDefault();
    try {
      const res = await axios.post(`${process.env.REACT_APP_API_URL}/login/`, {
        email: formData.email,
        password: formData.password,
      });
      // Instead of directly setting localStorage, call the context login
      login(res.data.access, res.data.refresh, formData.email);
      alert("Login Successful");
      navigate("/profile");
    } catch (err) {
      const errorMsg = err.response?.data?.non_field_errors?.[0] ||
        err.response?.data?.detail ||
        "Invalid credentials";
      alert(errorMsg);
    }
  };

  // Forgot password handler
  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      await axios.post(`${process.env.REACT_APP_API_URL}/password-reset/`, {
        email: formData.email,
      });
      alert("If an account exists with this email, a reset link has been sent.");
      setActiveForm("login");
    } catch (err) {
      alert("Something went wrong. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  // similar change for handleGoogleSuccess
  const handleGoogleSuccess = async (credentialResponse) => {
    try {
      const res = await axios.post(
        `${process.env.REACT_APP_API_URL}/google-login/`,
        { credential: credentialResponse.credential }
      );
      login(res.data.access, res.data.refresh, res.data.user.email);
      alert("Google login successful");
      navigate("/profile");
    } catch (err) {
      alert("Google login failed.");
    }
  };

  const handleGoogleError = () => {
    alert("Google login failed.");
  };

  if (activeForm === "reset") {
    return (
      <GoogleOAuthProvider clientId={GOOGLE_CLIENT_ID}>
        <ResetPassword />
      </GoogleOAuthProvider>
    );
  }

  return (
    <GoogleOAuthProvider clientId={GOOGLE_CLIENT_ID}>
      <section className="account-wrapper">
        {/* Animated Background */}
        <div className="auth-background">
          <div className="circle circle-1"></div>
          <div className="circle circle-2"></div>
          <div className="bubble bubble-1"></div>
          <div className="bubble bubble-2"></div>
          <div className="bubble bubble-3"></div>
        </div>

        <div className="account-content">
          <div className={`container container-sm ${activeForm === "register" ? "wider" : ""}`}>
            {/* Removed toggle buttons section */}

            {/* Form Container */}
            <div className="form-card">
              <div className={`form-container ${activeForm === "register" ? "register-active" : ""}`}>

                {/* Login Form */}
                {activeForm === "login" && (
                  <div className="form-section login-form active">
                    <h4 className="form-title">Welcome Back!</h4>
                    <p className="form-subtitle">Login to your account</p>
                    <form className="auth-form" onSubmit={handleLogin}>
                      <div className="form-group">
                        <label className="form-label">Email Address</label>
                        <div className="input-wrapper">
                          <input
                            type="email"
                            className="form-input"
                            placeholder="Enter your email"
                            name="email"
                            value={formData.email}
                            onChange={handleChange}
                            required
                          />
                        </div>
                      </div>
                      <div className="form-group">
                        <label className="form-label">Password</label>
                        <div className="input-wrapper">
                          <input
                            type={showPassword ? "text" : "password"}
                            className="form-input"
                            placeholder="Create password"
                            name="password"
                            value={formData.password}
                            onChange={handleChange}
                            required
                          />
                          <span
                            className="show-password-icon"
                            onClick={() => setShowPassword((v) => !v)}
                            style={{ position: "absolute", right: 18, cursor: "pointer", zIndex: 2 }}
                            tabIndex={0}
                          >
                            {showPassword ? <FaEyeSlash /> : <FaEye />}
                          </span>
                        </div>
                      </div>


                      <div className="form-options">
                        <label className="checkbox-wrapper">
                          <input type="checkbox" />
                          <span>Remember me</span>
                        </label>
                        <button
                          type="button"
                          className="forgot-link"
                          onClick={() => setActiveForm("forgot")}
                          style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer' }}
                        >
                          Forgot Password?
                        </button>
                      </div>
                      <button type="submit" className="submit-btn">
                        Login Now
                      </button>
                    </form>
                    <div className="google-login-container">
                      <GoogleLogin onSuccess={handleGoogleSuccess} onError={handleGoogleError} />
                    </div>
                    <p className="switch-form-text">
                      Don't have an account?{" "}
                      <button className="switch-form-btn" onClick={() => setActiveForm("register")}>Sign up</button>
                    </p>
                  </div>
                )}

                {/* Forgot Password Form */}
                {activeForm === "forgot" && (
                  <div className="form-section forgot-form active">
                    <h4 className="form-title">Reset Password</h4>
                    <p className="form-subtitle">Enter your email to receive a reset link</p>
                    <form className="auth-form" onSubmit={handleForgotPassword}>
                      <div className="form-group">
                        <label className="form-label">Email Address</label>
                        <div className="input-wrapper">
                          <input
                            type="email"
                            className="form-input"
                            placeholder="Enter your email"
                            name="email"
                            value={formData.email}
                            onChange={handleChange}
                            required
                          />
                        </div>
                      </div>
                      <button type="submit" className="submit-btn" style={{ marginTop: '20px' }} disabled={isSubmitting}>
                        {isSubmitting ? "Sending..." : "Send Reset Link"}
                      </button>
                    </form>
                    <p className="switch-form-text">
                      Remembered your password?{" "}
                      <button className="switch-form-btn" onClick={() => setActiveForm("login")}>Login</button>
                    </p>
                  </div>
                )}


                {/* Register Form */}
                {activeForm === "register" && (
                  <div className="form-section register-form active">
                    <h4 className="form-title">Create Account</h4>
                    <p className="form-subtitle">Sign up to get started</p>
                    <form className="auth-form" onSubmit={handleRegister}>
                      <div className="form-row">
                        <div className="form-group">
                          <label className="form-label">Full Name</label>
                          <div className="input-wrapper">
                            <input
                              type="text"
                              className="form-input"
                              placeholder="Enter your name"
                              name="name"
                              value={formData.name}
                              onChange={handleChange}
                              required
                            />
                          </div>
                        </div>
                        <div className="form-group">
                          <label className="form-label">Email Address</label>
                          <div className="input-wrapper">
                            <input
                              type="email"
                              className="form-input"
                              placeholder="Enter your email"
                              name="email"
                              value={formData.email}
                              onChange={handleChange}
                              required
                            />
                          </div>
                        </div>
                      </div>
                      <div className="form-row">
                        <div className="form-group">
                          <label className="form-label">Phone Number</label>
                          <div className="input-wrapper">
                            <input
                              type="tel"
                              className="form-input"
                              placeholder="Enter your phone"
                              name="phone"
                              value={formData.phone}
                              onChange={handleChange}
                              required
                            />
                          </div>
                        </div>
                        <div className="form-group">
                          <label className="form-label">Password</label>
                          <div className="input-wrapper">
                            <input
                              type={showPassword ? "text" : "password"}
                              className="form-input"
                              placeholder="Create password"
                              name="password"
                              value={formData.password}
                              onChange={handleChange}
                              required
                            />
                            <span
                              className="show-password-icon"
                              onClick={() => setShowPassword((prev) => !prev)}
                              tabIndex={0}
                            >
                              {showPassword ? <FaEyeSlash /> : <FaEye />}
                            </span>
                          </div>
                        </div>
                      </div>
                      <div className="form-group">
                        <label className="form-label">Confirm Password</label>
                        <div className="input-wrapper">
                          <input
                            type={showConfirmPassword ? "text" : "password"}
                            className="form-input"
                            placeholder="Confirm password"
                            name="confirmPassword"
                            value={formData.confirmPassword}
                            onChange={handleChange}
                            required
                          />
                          <span
                            className="show-password-icon"
                            onClick={() => setShowConfirmPassword((prev) => !prev)}
                            tabIndex={0}
                          >
                            {showConfirmPassword ? <FaEyeSlash /> : <FaEye />}
                          </span>
                        </div>
                      </div>
                      <div className="form-options">
                        <label className="checkbox-wrapper">
                          <input type="checkbox" required />
                          <span>I agree to the <a href="/terms-conditions">Terms & Conditions</a></span>
                        </label>
                      </div>
                      <button type="submit" className="submit-btn">
                        Create Account
                      </button>
                    </form>
                    <p className="switch-form-text">
                      Already have an account?{" "}
                      <button className="switch-form-btn" onClick={() => setActiveForm("login")}>Login</button>
                    </p>
                  </div>
                )}
              </div>
            </div>

          </div>
        </div>
      </section>
    </GoogleOAuthProvider>
  );
};

export default Account;
